
/****************************************************************************************************************
* ©2018 EdgeVerve Systems Limited, Bangalore, India. All Rights Reserved.
*
* The EdgeVerve proprietary software program ("Program"), is protected by copyrights laws, international treaties and
* other pending or existing intellectual property rights in India, the United States and other countries. The Program
* may contain / reference third party or open source components, the rights to which continue to remain with the
* applicable third party licensors or the open source community as the case may be and nothing here transfers the
* rights to the third party and open source components, except as expressly permitted. Any unauthorized reproduction,
* storage, transmission in any form or by any means (including without limitation to electronic, mechanical, printing,
* photocopying, recording or otherwise), or any distribution of this Program, or any portion of it, may result in
* severe civil and criminal penalties, and will be prosecuted to the maximum extent possible under the law.
*****************************************************************************************************************/

import { Injectable } from '@angular/core';

import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { Constants } from '../ba-supplier-ui.constants';
import { MatSnackBar } from '@angular/material/snack-bar';

@Injectable()
export class SuppUserService {

    private getUserRolesUrl = Constants.API_SUP_URL + 'suppliers/getUserRole';
    private getRoleCodesUrl = Constants.API_SUP_URL + 'suppliers/getRoleCodes';
    private createUserUrl = Constants.API_SUP_URL + 'suppliers/createuser';
    private updateUserUrl = Constants.API_SUP_URL + 'suppliers/updateuser';
    private updateUserRoleUrl = Constants.API_SUP_URL + 'suppliers/updateuserrole';
    private supplierApiBasePath = Constants.API_SUP_URL + 'suppliers/';
    private createSupplierUserMappingUrl = this.supplierApiBasePath + 'mapuser';
    private getUsersUrl = Constants.API_SUP_URL + 'suppliers/secusers';
    // private getUserByIdUrl = Constants.API_AUTH_URL + '/getUserById';
    private deleteUserUrl = Constants.API_SUP_URL + 'suppliers/deleteuser';
    private locationUrl = Constants.API_COMMON_URL + Constants.REFERENCEDATA_BASE_URL;
    private searchLocationUrl = this.locationUrl + '/searchLocations?q=';
    private getLocationUrl = this.locationUrl + '/getLocation?locationId=';
    private getProfileUrl = Constants.API_SUP_URL + 'suppliers/userprofile';
    private updateProfileUrl = Constants.API_SUP_URL + 'suppliers/updateprofile';
    private groupsUrl = Constants.API_COMMON_COMMON_URL + 'groups/';
    private getGroupsUrl = this.groupsUrl + 'getGroups';
    private getMyGroupsUrl = this.groupsUrl + 'getGroupsByUser';
    private addUserToGroupUrl = this.groupsUrl + 'addUserToGroup';
    private deleteUserFromGroupUrl = this.groupsUrl + 'removeUserFromGroup';
    private getGroupByCodeUrl = this.groupsUrl + 'getGroupByCode';
    private updateUserGroupsUrl = this.groupsUrl + 'updateUserGroups';
    private getAppCodesUrl = Constants.API_SUP_URL + 'suppliers/getApps';

    constructor(private httpClient: HttpClient, public snackBar: MatSnackBar) { }

    getUsers(data): Observable<any> {
        if (data.skip >= 0 && data.limit) {
            return this.httpClient.get<any>(this.getUsersUrl + '?q=' + (data.searchText ? data.searchText : '')
            + '&skip=' + data.skip + '&limit=' + data.limit);
        } else {
            return this.httpClient.get<any>(this.getUsersUrl + '?q=' + (data.searchText ? data.searchText : ''));
        }
    }

    getUserProfile(): Observable<any> {
        return this.httpClient.get(this.getProfileUrl);
    }

    updateSupplierProfile(data): any {
        return this.httpClient.post<any>(this.updateProfileUrl, data);
    }

    getAppList(): any {
        return this.httpClient.get<any>(this.getAppCodesUrl);
    }

    getUserRoles(id): Observable<any> {
        return this.httpClient.get<any>(this.getUserRolesUrl + '/' + id);
    }

    getRoleCodes(): Observable<any> {
        return this.httpClient.get<any>(this.getRoleCodesUrl);
    }
    updateUserRoles(data): Observable<any> {
        return this.httpClient.post<any>(this.updateUserRoleUrl, data);
    }

    createSupplierUser(data): Observable<any> {
        return this.httpClient.post<any>(this.createUserUrl, data);
    }
    updateSupplierUser(data): Observable<any> {
        return this.httpClient.post<any>(this.updateUserUrl, data);
    }
    deleteUser(data) {
        return this.httpClient.post<any>(this.deleteUserUrl, data);
    }
    getLocationSuggestions(location: string): Observable<any> {
        return this.httpClient.get<any>(this.searchLocationUrl + location + '&limit=10');
    }
    getLocation(locationId) {
        return this.httpClient.get<any>(this.getLocationUrl + locationId);
    }
    getCountries(): Observable<any> {
        const countryApiPath = Constants.API_COMMON_URL + '/country/getCountries';
        return this.httpClient.get<any>(countryApiPath);
    }

    getStates(countryIso2Code: string): Observable<any> {
        const countryApiPath = Constants.API_COMMON_URL + '/state/getStates';
        const params = '?iso2CountryCode=' + countryIso2Code;
        return this.httpClient.get<any>(countryApiPath + params);
    }

    getCities(countryIso2Code: string, stateCode: string): Observable<any> {
        const countryApiPath = Constants.API_COMMON_URL + '/city/getCities';
        const params = '?iso2CountryCode=' + countryIso2Code + '&stateCode=' + stateCode;
        return this.httpClient.get<any>(countryApiPath + params);
    }
    getQuotesAssignedToSupplier(q = '', skip = 0, limit = 10, sortField = '', order = '', filters = {}) {
        let url = this.supplierApiBasePath + 'quotes?skip=' + skip.toString() + '&limit=' + limit.toString();
        url = (typeof q === 'string' && q.toString().trim().length > 0) ? url.concat('&q=').concat(q.toString().trim()) : url;
        url = (typeof sortField === 'string' && sortField.toString().trim().length > 0) ?
            url.concat('&sortField=').concat(sortField.toString().trim()) : url;
        url = (typeof order === 'string' && order.toString().trim().length > 0) ?
            url.concat('&order=').concat(order.toString().trim()) : url;
        if (typeof filters === 'object' && Object.keys(filters).length > 0) {
            // tslint:disable-next-line:forin
            for (let key in filters) {
                url = url.concat('&filter=').concat(key.toString().concat(':').concat(
                    encodeURIComponent(filters[key].toString())));
            }
        }
        return this.httpClient.get(url);
    }
    getQuoteById(quoteId) {
        return this.httpClient.get(this.supplierApiBasePath.concat('quotes/').concat(quoteId.toString().trim()));
    }
    addResponseToQuote(reqData) {

        // let headers = new HttpHeaders({ 'Content-Type': 'application/json' });
        const apiurl: any = this.supplierApiBasePath + 'quotes/addResponse';
        return this.httpClient.post(apiurl, reqData);
    }

    getGroups(data): Observable<any> {
        if (data.skip >= 0 && data.limit) {
            return this.httpClient.get<any>(this.getGroupsUrl + '?skip=' + data.skip + '&limit=' + data.limit);
        } else {
            return this.httpClient.get<any>(this.getGroupsUrl);
        }
    }

    getMyGroups(data): Observable<any> {
        if (data.skip >= 0 && data.limit) {
            return this.httpClient.get<any>(this.getMyGroupsUrl + '?userId=' + data.userId + '&skip=' + data.skip + '&limit=' + data.limit);
        } else {
            return this.httpClient.get<any>(this.getMyGroupsUrl + '?userId=' + data.userId);
        }
    }
    addUserToGroup({ userId, groupId }) {
        return this.httpClient.post<any>(this.addUserToGroupUrl, { userId: userId, groupId: groupId });
    }

    removeUserFromGroup({ userId, groupId }) {
        return this.httpClient.post<any>(this.deleteUserFromGroupUrl, { userId: userId, groupId: groupId });
    }

    getGroupByCode({ groupCode }) {
        return this.httpClient.get<any>(this.getGroupByCodeUrl + '?Group%20Code=' + groupCode);
    }

    updateUserGroups({ userId, groups }) {
        return this.httpClient.post<any>(this.updateUserGroupsUrl, { userId: userId, groups: groups });
    }

    openSnackBar(message: string) {
        this.snackBar.open(message, 'Close', {
            duration: 2000
        });
    }
}



